import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react'

import {
  ChevronLeft,
  ChevronRight,
  Heading,
  Text,
  theme,
} from '@hhgtech/hhg-components'

import { PopoverMenu, Props as PopoverMenuProps } from './index'
import { StyledTree } from './index.styled'

export type TreeProps = {
  data?: TreeDataProps
  setShow?: Dispatch<SetStateAction<boolean>>
}
export type Props = PopoverMenuProps & TreeProps

export type TreeDataProps = {
  title?: string
  description?: string
  isShowheader?: boolean
  type: 'jsx' | 'list'
  jsxRenderer?: (setShow?: (v: boolean) => void) => ReactNode
  list?: ListItemProps[]
} | null

export type ListItemProps = {
  icon?: string | null
  label: string
  onClick?: () => void
  subTree?: TreeDataProps
  selected?: boolean
  style?: CSSProperties
  className?: string
  dataEventCategory?: string
  dataEventAction?: string
  dataEventLabel?: string
}

export const TreePopoverMenu = ({ data, ...popoverProps }: Props) => {
  return (
    <PopoverMenu {...popoverProps}>
      {({ setShow }) => <Tree data={data} setShow={setShow} />}
    </PopoverMenu>
  )
}

const getCurrentDataFromPath = (
  data: TreeDataProps,
  path: string,
): TreeDataProps | undefined => {
  const pathItems = path.split('.')
  let currentData: TreeDataProps | undefined = data
  pathItems.forEach((pathItem) => {
    if (!pathItem || !currentData || currentData?.type !== 'list') return
    currentData = currentData?.list?.[Number(pathItem)]?.subTree
  })
  return currentData
}

export const Tree = ({ data, setShow }: TreeProps) => {
  const [path, setPath] = useState('')
  const currentData = useMemo(
    () => data && getCurrentDataFromPath(data, path),
    [data, path],
  )

  if (!data) return null
  if (!currentData) return null

  const handleItemClick = (
    index: number,
    { subTree, onClick = () => null }: ListItemProps,
  ) => {
    if (onClick) onClick()
    if (!subTree) setShow && setShow(false)
    else setPath((p) => p.split('.').concat(String(index)).join('.'))
  }

  return (
    <StyledTree>
      {currentData.isShowheader ? (
        <div className="__header">
          {path ? (
            <ChevronLeft
              className="__back-btn"
              color="#595959"
              onClick={() =>
                setPath((p) => p.split('.').slice(0, -1).join('.'))
              }
            />
          ) : null}
          {currentData.title ? (
            <Heading className="__title" tag="h6">
              {currentData.title}
            </Heading>
          ) : null}
        </div>
      ) : null}
      {currentData.description ? (
        <Text
          className="tree-text-description"
          color={theme.colors.gray600}
          size="p3"
          type="regular"
        >
          {currentData.description}
        </Text>
      ) : null}
      <div className="__content">
        {currentData.type === 'jsx'
          ? currentData?.jsxRenderer?.(setShow)
          : null}
        {currentData.type === 'list' ? (
          <ul className="__list">
            {Array.isArray(currentData.list)
              ? currentData.list.map((item: ListItemProps, index) => (
                  <li
                    key={index}
                    style={item.style}
                    className={`__item ${item.className}`}
                  >
                    <div
                      className={`__btn ${item.selected ? '--checked' : ''}`}
                      onClick={() => handleItemClick(index, item)}
                      data-event-category={item.dataEventCategory}
                      data-event-action={item.dataEventAction}
                      data-event-label={item.dataEventLabel}
                    >
                      {item.icon && (
                        <img
                          loading="lazy"
                          className="__icon"
                          src={item.icon}
                        />
                      )}
                      <span>{item.label}</span>
                    </div>
                    {item.subTree ? (
                      <ChevronRight className="__toggle" color="#595959" />
                    ) : null}
                    {item.selected ? (
                      <ChevronRight className="__toggle" color="#8C8C8C" />
                    ) : null}
                  </li>
                ))
              : null}
          </ul>
        ) : null}
      </div>
    </StyledTree>
  )
}
